import { c as _c } from "react/compiler-runtime";
import type { ErrorDetail } from '@onefootprint/request-types/dashboard';
import { Dialog, InlineAlert, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useTranslateCategory } from '@/components/playbook-builder/hooks/use-translate-category';
import type { ApplyEditMutation } from '../../base-node';
import { ReadOnlyBranchDisplay } from '../../shared/read-only-branch';
import type { BranchesToRemoveByCategory } from '../../shared/rule-suggestion-context.types';
type RemoveUnallowedRulesProps = {
  error: ErrorDetail;
  ruleSet: BranchesToRemoveByCategory;
  nodeId: string;
  mutation: ApplyEditMutation;
  type: 'error' | 'warning' | 'info';
};
export const RemoveUnallowedRules = t0 => {
  const $ = _c(57);
  const {
    error,
    type,
    ruleSet,
    nodeId,
    mutation
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rules.removeUnallowedRules"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    isAnyComponentDirty
  } = useDirty();
  const [open, setIsOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsOpen(true);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleShowConfirmation = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsOpen(false);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleClose = t3;
  let t4;
  if ($[3] !== mutation || $[4] !== nodeId || $[5] !== ruleSet.category) {
    t4 = () => {
      mutation.mutateAsync({
        kind: "update_node",
        nodeId,
        nodeData: {
          kind: "remove_rules_for_category",
          category: ruleSet.category
        }
      });
    };
    $[3] = mutation;
    $[4] = nodeId;
    $[5] = ruleSet.category;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleConfirmAddRules = t4;
  const categoryStr = useTranslateCategory(ruleSet.category);
  let t5;
  if ($[7] !== categoryStr || $[8] !== isAnyComponentDirty || $[9] !== t) {
    t5 = isAnyComponentDirty ? t(_temp, {
      categoryStr
    }) : undefined;
    $[7] = categoryStr;
    $[8] = isAnyComponentDirty;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const disabledTooltipText = t5;
  const isDisabled = isAnyComponentDirty || mutation.isPending;
  let t6;
  if ($[11] !== t) {
    t6 = t(_temp2);
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t(_temp3);
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== handleConfirmAddRules || $[16] !== mutation.isPending || $[17] !== t7) {
    t8 = {
      label: t7,
      loading: mutation.isPending,
      onClick: handleConfirmAddRules
    };
    $[15] = handleConfirmAddRules;
    $[16] = mutation.isPending;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t) {
    t9 = t(_temp4);
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== mutation.isPending || $[22] !== t9) {
    t10 = {
      label: t9,
      disabled: mutation.isPending,
      onClick: handleClose
    };
    $[21] = mutation.isPending;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== ruleSet) {
    t11 = <RemoveUnallowedRulesDialog ruleSet={ruleSet} />;
    $[24] = ruleSet;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== open || $[27] !== t10 || $[28] !== t11 || $[29] !== t6 || $[30] !== t8) {
    t12 = <Dialog isConfirmation={true} open={open} onClose={handleClose} title={t6} primaryButton={t8} secondaryButton={t10}>{t11}</Dialog>;
    $[26] = open;
    $[27] = t10;
    $[28] = t11;
    $[29] = t6;
    $[30] = t8;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== error.message) {
    t13 = <span>{error.message}</span>;
    $[32] = error.message;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = !disabledTooltipText;
  const t15 = ruleSet.category;
  const t16 = !isDisabled;
  let t17;
  if ($[34] !== isDisabled || $[35] !== t16) {
    t17 = cx(["ml-1 underline cursor-pointer", {
      "hover:opacity-70 active:opacity-85": t16,
      "opacity-50 cursor-not-allowed": isDisabled
    }]);
    $[34] = isDisabled;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = isAnyComponentDirty || mutation.isPending;
  let t19;
  if ($[37] !== t) {
    t19 = t(_temp5);
    $[37] = t;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== ruleSet.category || $[40] !== t17 || $[41] !== t18 || $[42] !== t19) {
    t20 = <button type="button" key={t15} onClick={handleShowConfirmation} className={t17} disabled={t18}>{t19}</button>;
    $[39] = ruleSet.category;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== disabledTooltipText || $[45] !== t14 || $[46] !== t20) {
    t21 = <Tooltip content={disabledTooltipText} disabled={t14}>{t20}</Tooltip>;
    $[44] = disabledTooltipText;
    $[45] = t14;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t13 || $[49] !== t21) {
    t22 = <div className="text-warning text-body-2">{t13}{t21}</div>;
    $[48] = t13;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t22 || $[52] !== type) {
    t23 = <InlineAlert variant={type}>{t22}</InlineAlert>;
    $[51] = t22;
    $[52] = type;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t12 || $[55] !== t23) {
    t24 = <>{t12}{t23}</>;
    $[54] = t12;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  return t24;
};
const RemoveUnallowedRulesDialog = t0 => {
  const $ = _c(14);
  const {
    ruleSet
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rules.removeUnallowedRules.confirm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const categoryStr = useTranslateCategory(ruleSet.category);
  let t2;
  if ($[1] !== categoryStr || $[2] !== t) {
    t2 = t(_temp6, {
      categoryStr
    });
    $[1] = categoryStr;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="text-body-2">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ruleSet.branches || $[7] !== t) {
    let t5;
    if ($[9] !== t) {
      t5 = branch => <ReadOnlyBranchDisplay key={branch.branchName} branchNameLabel={t(_temp7)} branchName={branch.branchName} rules={branch.rules.map(_temp8)} itemsLabel={t(_temp9)} />;
      $[9] = t;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = ruleSet.branches.map(t5);
    $[6] = ruleSet.branches;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex flex-col gap-4">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
function _temp($) {
  return $.disabledTooltip;
}
function _temp2($_0) {
  return $_0.confirm.title;
}
function _temp3($_1) {
  return $_1.confirm.confirm;
}
function _temp4($_2) {
  return $_2.confirm.cancel;
}
function _temp5($_3) {
  return $_3.cta;
}
function _temp6($) {
  return $.description;
}
function _temp7($_0) {
  return $_0.branch;
}
function _temp8(rule) {
  return rule.ruleExpression;
}
function _temp9($_1) {
  return $_1.itemsLabel;
}